/**
 * Offers Table Component
 * Displays list of active funding offers
 */
import type { OfferDetail } from '../../types/botMonitoring'
import { formatCurrency, formatRate, formatTimestamp } from '../../utils/formatters'
import LoadingSpinner from '../Common/LoadingSpinner'
import EmptyState from '../Common/EmptyState'

interface OffersTableProps {
  offers: OfferDetail[]
  currency: string
  isLoading?: boolean
  error?: Error | null
}

export default function OffersTable({ offers, currency, isLoading, error }: OffersTableProps) {
  // Loading state
  if (isLoading) {
    return <LoadingSpinner message="載入掛單資料..." />
  }

  // Error state
  if (error) {
    return (
      <EmptyState
        icon={
          <svg
            className="mx-auto h-12 w-12 text-red-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        }
        title="載入失敗"
        description="無法載入掛單資料，請稍後再試"
      />
    )
  }

  // Empty state
  if (offers.length === 0) {
    return (
      <EmptyState
        title="目前無掛單"
        description="Bot 正在分析市場，稍後會自動掛單"
      />
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              掛單 ID
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              金額
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              利率 (APR)
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              期限
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              狀態
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              創建時間
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {offers.map((offer) => (
            <tr key={offer.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                #{offer.id}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {formatCurrency(offer.amount, currency)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                {formatRate(offer.rate)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {offer.period} 天
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    offer.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : offer.status === 'cancelled'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {offer.status === 'active'
                    ? '活躍'
                    : offer.status === 'cancelled'
                      ? '已取消'
                      : offer.status === 'executed'
                        ? '已成交'
                        : '部分成交'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatTimestamp(offer.created_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
